import React from 'react';
import styled from "styled-components";
import Colours from '@shared/ui/atoms/Colours';
import { useDispatch, useSelector } from "react-redux";
import { CarParkPlanBooking, CarParkSpace } from '@features/whereabouts/services/CarParkingPlanService';
import IconButton from '@shared/ui/molecules/icon-button/IconButton';
import { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { Period, SideOfDay } from '@features/whereabouts/models/movements.models';
import { Row } from '@shared/ui/atoms/StructuralLayout';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { DialogIdentifiers, openDialogWithPayload } from "@shared/store/dialog.duck";
import { selectCurrentUser } from "@shared/store/auth.duck";
import { isCompanyAdminUser, isOfficeAdminUser } from "@shared/utils/AccessControl";
import { bookCarParkSpace, cancelCarParkBooking, selectIsLoading } from "../../ducks/carParkingMapView.duck";
import { selectActiveDay } from "../../../../store/editMovements.duck";
import { DATE_FORMAT } from "@shared/utils/DateUtils";
import { IsSuperAdmin } from "@shared/hooks/IsSuperAdmin";
import { setSelectedCarParkSpace } from "../../../../store/officeView.duck";
import { WithParkingSpaceBooking } from "./WithParkingSpaceBooking";
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';


interface Props {
  carParkingSpace?: CarParkSpace;
  carParkBookings: CarParkPlanBooking[];
}

export function SelectedCarParkingSpaceOverlay(props: Props) {
  const { carParkingSpace, carParkBookings } = props;
  const currentUser = useSelector(selectCurrentUser);
  const isAdmin = isCompanyAdminUser(currentUser) || isOfficeAdminUser(currentUser);
  const activeDate = useSelector(selectActiveDay);
  const isLoading = useSelector(selectIsLoading);
  const isSuperAdmin = IsSuperAdmin();
  const dispatch = useDispatch();
  const {amBooking, pmBooking, allDayBooking, isBooked, canControlBooking} = WithParkingSpaceBooking(carParkBookings, carParkingSpace);

  const canControlAmBookingValue = canControlBooking(currentUser, SideOfDay.AM);
  const canControlPmBookingValue = canControlBooking(currentUser, SideOfDay.PM);

  const close = () => {
    dispatch(setSelectedCarParkSpace(undefined));
  }

  const onEditClick = () => {
    if (carParkingSpace) {
      dispatch(openDialogWithPayload({
        payload: { parkingSpace: carParkingSpace },
        activeDialog: DialogIdentifiers.EditParkingSpaceDialog,
      }));
      close();
    }
  }

  const onCopySpaceClicked = () => {
    dispatch(openDialogWithPayload({
      payload: { parkingSpace: {...carParkingSpace, id: 0, label: 'New space'} },
      activeDialog: DialogIdentifiers.EditParkingSpaceDialog,
    }));
    close();
  }

  const onBookSpace = (period: Period) => {
    if (!currentUser || !activeDate || !carParkingSpace) {
      return;
    }
    const params = {
      period: period,
      userId: currentUser.id,
      dateString: activeDate.format(DATE_FORMAT),
      carParkPlanId: carParkingSpace.carParkPlanId,
      carParkingSpaceId: carParkingSpace.id,
    }
    dispatch(bookCarParkSpace(params));
  }

  const onCancelBookingClicked = (userId: string) => {
    if (!currentUser || !activeDate || !carParkingSpace) {
      return;
    }
    const params = {
      userId: userId,
      dateString: activeDate.format(DATE_FORMAT),
      carParkPlanId: carParkingSpace.carParkPlanId,
      carParkingSpaceId: carParkingSpace.id,
    }
    dispatch(cancelCarParkBooking(params));
  }

  if (!carParkingSpace) {
    return null;
  }

  return (
    <Popup style={{ left: carParkingSpace.x + 50, top: carParkingSpace.y - 0 }} onClick={(e: any) => e.stopPropagation()}>

      <ControlHeader>
        {isSuperAdmin && <IconButton icon={IconTypes.Copy} onClick={onCopySpaceClicked} />}
        {isAdmin && <IconButton icon={IconTypes.Edit} onClick={onEditClick} />}
      </ControlHeader>
      <PopupContent>

        {isBooked && <>
          {allDayBooking ? <BodyRegular weight={600}>{allDayBooking.name}</BodyRegular> : <>
            <BodyRegular weight={600}>AM: {amBooking ? amBooking.name : 'Available'}</BodyRegular>
            <BodyRegular weight={600}>PM: {pmBooking ? pmBooking.name : 'Available'}</BodyRegular>
          </>}
        </>}

        <BodyRegular weight={600}>{carParkingSpace.label}</BodyRegular>


        {isBooked && (canControlAmBookingValue || canControlPmBookingValue) && <>
          {allDayBooking ? <>
            <OutlineButton click={() => onCancelBookingClicked(allDayBooking.userId)}
                           text={'car-parking.cancel-booking'}
                           disabled={isLoading}
                           style={{marginTop: 16, marginBottom: 0}}
                           fullWidth={true} />
          </> : <>
            <Row>
              {amBooking && canControlAmBookingValue && <OutlineButton click={() => onCancelBookingClicked(amBooking.userId)}
                                                                       text={'car-parking.am-cancel-booking'}
                                                                       disabled={isLoading}
                                                                       style={{marginTop: 16, marginBottom: 0, marginRight: 4}}
                                                                       fullWidth={true} />}
              {pmBooking && canControlPmBookingValue && <OutlineButton click={() => onCancelBookingClicked(pmBooking.userId)}
                                                                       text={'car-parking.pm-cancel-booking'}
                                                                       disabled={isLoading}
                                                                       style={{marginTop: 16, marginBottom: 0, marginLeft: 4}}
                                                                       fullWidth={true} />}
            </Row>
          </>}
        </>}

        <PrimaryButton click={(e: any) => onBookSpace(Period.AllDay)}
                       disabled={isBooked || isLoading}
                       size={ButtonSize.Medium}
                       style={{marginTop: 16, marginBottom: 16}}
                       fullWidth={true}
                       text="button.book-all-day" />

        <Row style={{marginBottom: 16}}>
          <OutlineButton disabled={!!amBooking || isLoading}
                         text="button.book-am"
                         style={{marginRight: 8}}
                         size={ButtonSize.Small}
                         click={(e: Event) => onBookSpace(Period.AM)}  />
          <OutlineButton disabled={!!pmBooking || isLoading}
                         text="button.book-pm"
                         style={{marginLeft: 8}}
                         size={ButtonSize.Small}
                         click={(e: Event) => onBookSpace(Period.PM)} />
        </Row>
      </PopupContent>
    </Popup>
  )
}

const ControlHeader = styled.div<any>`
  background-color: ${Colours.lightGrey};
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  position: relative;
  padding: 8px 24px;
`

const Popup = styled.div<any>`
  width: 260px;
  min-height: 120px;
  background-color: white;
  border: 1px solid ${Colours.lightGrey};
  box-shadow: 0 22px 24px 2px rgba(0, 87, 255, 0.15);
  border-radius: 16px;
  position: absolute;
  z-index: 15000;
  overflow: hidden;
`

const PopupContent = styled.div<any>`
  padding: 24px;
  display: flex;
  justify-content: center;
  flex-direction: column;
  width: 100%;
`
